<template>
  <div class="results2-wrap">
    <main class="screen" role="main">

      <div class="header-row">
        <span class="badge">Dein Ergebnis</span>
        <div>
          <span class="score-inline">{{ overallScore }}</span>
          <span class="score-max">/ {{ overallMax }}</span>
        </div>
      </div>

      <p class="verbal-label">{{ verbalLabel }}</p>

      <div class="radar-container">
        <svg viewBox="0 0 320 320" class="radar-svg" :aria-label="`Radar mit ${groups.length} Lebensmittelgruppen`">
          <circle
            v-for="r in [25, 50, 75, 100]"
            :key="`ring-${r}`"
            cx="160"
            cy="160"
            :r="(r / 100) * radarRadius"
            fill="none"
            stroke="rgba(0,0,0,0.1)"
            stroke-width="1"
          />
          <line
            v-for="(p, i) in radar.axes"
            :key="`ax-${i}`"
            x1="160"
            y1="160"
            :x2="p.x"
            :y2="p.y"
            stroke="rgba(0,0,0,0.1)"
            stroke-width="1"
          />
          <polygon
            :points="radar.polygon"
            fill="rgba(83, 74, 183, 0.2)"
            stroke="#534AB7"
            stroke-width="2"
          />
          <circle
            v-for="(p, i) in radar.points"
            :key="`pt-${i}`"
            :cx="p.x"
            :cy="p.y"
            r="4"
            fill="#534AB7"
          />
          <text
            v-for="(p, i) in radar.labels"
            :key="`lb-${i}`"
            :x="p.x"
            :y="p.y"
            text-anchor="middle"
            dominant-baseline="middle"
            font-size="11"
            fill="#5f5e5a"
          >{{ p.label }}</text>
        </svg>
      </div>

      <button type="button" class="meaning-link" @click="onMeaningClick">
        Was bedeutet das?
      </button>

      <div class="card good">
        <p class="card-title">✓ Deine Stärken</p>
        <div v-for="g in strengths" :key="g.id" class="group-row">
          <span class="emoji">{{ g.emoji }}</span>
          <span class="name">{{ g.name }}</span>
          <span class="score">{{ g.score }}</span>
        </div>
      </div>

      <div class="card warn">
        <p class="card-title">→ Wo du wachsen kannst</p>
        <div v-for="g in gaps" :key="g.id" class="group-row">
          <span class="emoji">{{ g.emoji }}</span>
          <span class="name">{{ g.name }}</span>
          <span class="score">{{ g.score }}</span>
        </div>
        <p class="suggestion-text">{{ gapsSummary }}</p>
      </div>

      <div class="footer">
        <button type="button" class="next-button" @click="goNext">Weiter</button>
      </div>

      <p class="placeholder-note">
        Beispieltexte. Die finalen Formulierungen werden vom Ernährungsteam erstellt.
      </p>

    </main>
  </div>
</template>

<script>
import { overallScore, overallMax, verbalLabel, groups } from '../_data/sampleResults.js';

const RADAR_CENTER = 160;
const RADAR_RADIUS = 100;
const LABEL_OFFSET = 22;

function axisAngle(i, n) {
  return (i / n) * 2 * Math.PI - Math.PI / 2;
}

export default {
  name: 'Results2',
  data() {
    return {
      overallScore,
      overallMax,
      verbalLabel,
      groups,
      radarRadius: RADAR_RADIUS,
      gapsSummary:
        'Mehr Hülsenfrüchte (Linsen, Kichererbsen, Hummus) und ein Wechsel von Weissbrot zu Vollkornbrot bringen schon viel. Diese zwei Veränderungen würden dein Gesamtergebnis spürbar heben.',
    };
  },
  computed: {
    strengths() {
      return [...this.groups]
        .filter(g => g.score >= 80)
        .sort((a, b) => b.score - a.score);
    },
    gaps() {
      return [...this.groups]
        .filter(g => g.score < 70)
        .sort((a, b) => a.score - b.score);
    },
    radar() {
      const n = this.groups.length;
      const axes = this.groups.map((_, i) => {
        const a = axisAngle(i, n);
        return {
          x: RADAR_CENTER + RADAR_RADIUS * Math.cos(a),
          y: RADAR_CENTER + RADAR_RADIUS * Math.sin(a),
        };
      });
      const points = this.groups.map((g, i) => {
        const a = axisAngle(i, n);
        const r = (g.score / 100) * RADAR_RADIUS;
        return {
          x: RADAR_CENTER + r * Math.cos(a),
          y: RADAR_CENTER + r * Math.sin(a),
        };
      });
      const labels = this.groups.map((g, i) => {
        const a = axisAngle(i, n);
        return {
          x: RADAR_CENTER + (RADAR_RADIUS + LABEL_OFFSET) * Math.cos(a),
          y: RADAR_CENTER + (RADAR_RADIUS + LABEL_OFFSET) * Math.sin(a),
          label: g.radarLabel || g.name,
        };
      });
      const polygon = points.map(p => `${p.x},${p.y}`).join(' ');
      return { axes, points, labels, polygon };
    },
  },
  methods: {
    goNext() {
      this.$router.push('/postsurvey/email');
    },
    onMeaningClick() {
      // Placeholder for "Was bedeutet das?"
    },
  },
};
</script>

<style scoped>
.results2-wrap {
  --color-bg-page: #f5f4ef;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-accent: #534AB7;
  --color-accent-light: #EEEDFE;
  --color-good: #639922;
  --color-good-light: #EAF3DE;
  --color-warn: #BA7517;
  --color-warn-light: #FAEEDA;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.5;
  min-height: 100%;
  padding: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

.results2-wrap *,
.results2-wrap *::before,
.results2-wrap *::after {
  box-sizing: border-box;
}

.screen {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem 1.25rem;
  min-height: 600px;
}

.header-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.badge {
  background: var(--color-accent-light);
  color: var(--color-accent);
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: var(--radius-md);
  letter-spacing: 0.02em;
}

.score-inline {
  font-size: 32px;
  font-weight: 500;
  color: var(--color-accent);
  line-height: 1;
}

.score-max {
  font-size: 14px;
  color: var(--color-text-tertiary);
  margin-left: 4px;
}

.verbal-label {
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text-primary);
  margin: 0 0 1.25rem;
}

.radar-container {
  background: var(--color-bg-muted);
  border-radius: var(--radius-lg);
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.radar-svg {
  width: 100%;
  max-width: 320px;
  height: auto;
  display: block;
}

.card {
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 14px 16px;
  margin-bottom: 12px;
  background: var(--color-bg-card);
}

.card.good {
  background: var(--color-good-light);
  border-color: var(--color-good);
}

.card.warn {
  background: var(--color-warn-light);
  border-color: var(--color-warn);
}

.card-title {
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card.good .card-title { color: var(--color-good); }
.card.warn .card-title { color: var(--color-warn); }

.group-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  font-size: 14px;
  color: var(--color-text-primary);
}

.group-row .emoji { font-size: 18px; line-height: 1; }
.group-row .name { flex: 1; }
.group-row .score { font-weight: 500; color: var(--color-text-secondary); }

.suggestion-text {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 0.5px solid var(--color-border);
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.meaning-link {
  font-size: 13px;
  color: var(--color-text-secondary);
  background: transparent;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-family: inherit;
  padding: 0;
  margin: 0 0 1.5rem;
}

.footer {
  margin-top: 1.5rem;
}

.next-button {
  width: 100%;
  padding: 14px;
  background: var(--color-accent);
  color: white;
  border: 0.5px solid var(--color-accent);
  border-radius: var(--radius-md);
  font-size: 15px;
  font-family: inherit;
  cursor: pointer;
}

.next-button:hover { opacity: 0.9; }

.placeholder-note {
  font-size: 11px;
  color: var(--color-text-tertiary);
  text-align: center;
  margin-top: 12px;
  font-style: italic;
}
</style>
